import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { AnalyticsPageName } from '@kanalytics';
import { FirstVisitBloc } from '@kbloc';
import { EnvironmentVariablesService } from '@kenv';
import { SocialChallengeBloc } from '@kf-sc';
import { DialogueBloc } from '@kp/dialogue/dialogue.bloc';
import { DialogueLogicKey } from '@kp/dialogue/models/dialogue.model';
import { QuestBloc } from '@kp/quest/quest.bloc';
import { SearchBloc } from '@kp/search/search.bloc';
import { CardBloc, CardType } from '@kp/shared/components/cards/card.bloc';
import { BrowserCheckService } from '@kp/shared/services/browser-check.service';
import { ThemeService } from '@kservice';
import { CardEventType, CardRequestType, CardScreen, Product } from '@ktypes/enums';
import { CardContentType, CardEvent, CardItem, Link, LinkType, Quest, QuestItem } from '@ktypes/models';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'kp-detail-card-view',
  templateUrl: './detail-card-view.component.html',
  styleUrls: ['./detail-card-view.component.scss'],
})
export class DetailCardViewComponent implements OnInit, AfterViewInit, OnDestroy {
  constructor(
    private _browserCheckService: BrowserCheckService,
    private _cardBloc: CardBloc,
    private _dialogueBloc: DialogueBloc,
    @Inject(DOCUMENT) private _document: Document,
    private _environmentVariablesService: EnvironmentVariablesService,
    private _firstVisitBloc: FirstVisitBloc,
    private _questBloc: QuestBloc,
    private _router: Router,
    private _searchBloc: SearchBloc,
    private _socialChallengeBloc: SocialChallengeBloc,
    public themeService: ThemeService
  ) {}

  @Input() card: CardItem;
  @Input() quest?: Quest;
  @Input() questItem?: QuestItem;
  @Input() requestType?: CardRequestType;
  @Input() screen = CardScreen.discover; // default to discover to fix compile

  @Output() modalClose = new EventEmitter<boolean>(); // True if deep link clicked, false otherwise

  analyticsPageName = AnalyticsPageName.cardDetail;
  cardContentType = CardContentType;
  cardScreen = CardScreen;
  cardEventType = CardEventType;
  challengeDomainKey?: string;
  isSafari = false;
  product = this._environmentVariablesService.product;
  returnUrl: string;
  isPurposeful = true;
  transcriptAccordionOpen = false;
  showHeaderShadow = false;

  @ViewChild('imageContainer') imageContainer: ElementRef;

  private _actionAddedStatus: boolean;
  private _questAddedStatus: boolean;
  private _destroy$ = new Subject<void>();
  private _observer: IntersectionObserver;

  ngOnInit() {
    this.isSafari = this._browserCheckService.deviceInfo.browser === 'Safari';
    this.isPurposeful = this._environmentVariablesService.product === Product.purposeful;
    this.returnUrl = this._cardBloc.getReturnUrlFromCardScreen(this.screen, this._router.url);
    this._firstVisitBloc.hasSeen$.pipe(takeUntil(this._destroy$)).subscribe((objStart) => {
      this._actionAddedStatus = objStart.firstActionAdded;
      this._questAddedStatus = objStart.firstQuestAdded;
    });
    this._socialChallengeBloc.currentChallenge$.pipe(takeUntil(this._destroy$)).subscribe((currentChallenge) => {
      this.challengeDomainKey = currentChallenge.domain.key;
    });
  }

  ngAfterViewInit() {
    this._observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.showHeaderShadow = entry.intersectionRatio !== 1;
        });
      },
      {
        threshold: 1,
      }
    );
    if (this.imageContainer) {
      this._observer.observe(this.imageContainer.nativeElement);
    }
  }

  ngOnDestroy() {
    this._observer?.disconnect?.();
    this._destroy$.next();
    this._destroy$.complete();
  }

  async linkClicked(event) {
    if (this.card?.inQuest && !this.card?.cardLink?.hasLink && !this.card?.userState?.isCompleted) {
      await this._cardBloc.completeQuestCard(this.card, this.quest);
      this._dialogueBloc.getDialogueStatus(DialogueLogicKey.focus_aware_daily_checkin_dialogue);
      this._questBloc.completeQuestIfDone(this.card, this.quest);
    }
    this._cardBloc.detailViewClicked.next({ detailViewClicked: false, card: null, screen: null });
    this._cardBloc.detailViewClosed.next(true);
    event.stopPropagation();
  }

  markUncomplete(event) {
    event.preventDefault();
    event.stopPropagation();
    void this._cardBloc.handleCardEvents(
      new CardEvent(this.card.id, this.card, CardEventType.UNCOMPLETE, this.requestType),
      null,
      false
    );
    this._dialogueBloc.getDialogueStatus(DialogueLogicKey.focus_aware_daily_checkin_dialogue);
  }

  markComplete(event) {
    event.preventDefault();
    event.stopPropagation();
    this.modalClose.emit(false);
    void this._cardBloc.handleCardEvents(
      new CardEvent(this.card.id, this.card, CardEventType.COMPLETE, this.requestType),
      null,
      false
    );
  }

  addToOrRemoveFromActions(event: Event, eventType: CardEventType) {
    event.preventDefault();
    event.stopPropagation();

    if (eventType === CardEventType.TRACK) {
      if (!this._actionAddedStatus && !this.card.isQuestCard) {
        this._cardBloc.firstCardAdded(CardType.action);
      }
      if (!this._questAddedStatus && this.card.isQuestCard) {
        this._cardBloc.firstCardAdded(CardType.quest);
      }
    }

    this.sendCardEvent(eventType);
  }

  sendCardEvent(eventType: CardEventType) {
    void this._cardBloc.handleCardEvents(
      new CardEvent(
        this.card.id,
        this.card,
        eventType,
        !this._searchBloc.isSearchState ? this.requestType : CardRequestType.SEARCH
      ),
      this.challengeDomainKey
    );
  }

  transcriptAccordionChange(isOpen) {
    this.transcriptAccordionOpen = isOpen;
  }

  get questActionType() {
    return this.card?.inQuest ? new Link().deserialize({ type: LinkType.ACTION }) : null;
  }
}
